import React, { useState } from 'react';
import { Edit3, MessageCircle, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = '/api';

const ContentTab = ({ contents, whatsappNumber, setWhatsappNumber, fetchData }) => {
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({ page_key: '', title: '', content: '' });

  const handleSaveContent = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/content`, contentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content saved successfully!');
      setEditingContent(null);
      setContentForm({ page_key: '', title: '', content: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const startEditingContent = (content) => {
    setEditingContent(content);
    setContentForm({
      page_key: content.page_key,
      title: content.title,
      content: content.content
    });
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Edit3 className="w-5 h-5 text-[#FF4500]" />
        Content Management
      </h3>
      
      {/* WhatsApp Settings */}
      <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg p-6 mb-6">
        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#25D366]" />
          WhatsApp Settings
        </h4>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
              WhatsApp Number (with country code, no + or spaces)
            </label>
            <input
              type="text"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value.replace(/\D/g, ''))}
              className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366]"
              placeholder="e.g., 12269297350"
            />
          </div>
          <button
            onClick={async () => {
              const token = localStorage.getItem('admin_token');
              try {
                await axios.put(`${API}/content/settings`, 
                  { whatsapp_number: whatsappNumber },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('WhatsApp number updated!');
              } catch (error) {
                toast.error('Failed to update WhatsApp number');
              }
            }}
            className="h-12 px-6 bg-[#25D366] text-white rounded-lg hover:bg-[#1EBE5A] transition-all duration-300 font-semibold"
          >
            Save
          </button>
        </div>
        <p className="text-xs text-[#525252] mt-2">
          Current link: wa.me/{whatsappNumber || '(not set)'}
        </p>
      </div>
      
      {!editingContent ? (
        <div>
          <button
            onClick={() => setEditingContent({ page_key: '', title: '', content: '' })}
            className="mb-4 bg-[#FF4500] text-white px-6 py-3 rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold"
          >
            Create New Content
          </button>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contents.map((content) => (
              <div key={content.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="font-semibold text-lg mb-2">{content.title || content.page_key}</h4>
                <p className="text-sm text-[#525252] mb-1">Page Key: {content.page_key}</p>
                <p className="text-xs text-[#999] mb-4">
                  Updated: {format(new Date(content.updated_at), 'MMM d, yyyy')}
                </p>
                <button
                  onClick={() => startEditingContent(content)}
                  className="flex items-center gap-2 text-[#FF4500] hover:text-[#E03E00] font-semibold"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit Content
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-semibold mb-4">
            {editingContent.id ? 'Edit Content' : 'Create New Content'}
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                Page Key (e.g., landing_hero, benefits_section)
              </label>
              <input
                type="text"
                value={contentForm.page_key}
                onChange={(e) => setContentForm({...contentForm, page_key: e.target.value})}
                className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter page key"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Title</label>
              <input
                type="text"
                value={contentForm.title}
                onChange={(e) => setContentForm({...contentForm, title: e.target.value})}
                className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Content</label>
              <textarea
                value={contentForm.content}
                onChange={(e) => setContentForm({...contentForm, content: e.target.value})}
                rows={10}
                className="w-full px-4 py-3 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter content (supports HTML)"
              />
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={handleSaveContent}
                className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-lg hover:bg-[#1EBE5A] transition-all duration-300 font-semibold"
              >
                <Save className="w-4 h-4" />
                Save Content
              </button>
              <button
                onClick={() => {
                  setEditingContent(null);
                  setContentForm({ page_key: '', title: '', content: '' });
                }}
                className="bg-gray-200 text-[#0A0A0A] px-6 py-3 rounded-lg hover:bg-gray-300 transition-all duration-300 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentTab;
